import React from 'react'
import { DayPicker } from 'react-day-picker'
import { enUS, fr } from 'react-day-picker/locale'
import { add, startOfMonth, sub } from 'date-fns'
import type { BookingSlot } from '~/@types/graphql-police'
import Button from '~/components/Button'
import IconButton from '~/components/IconButton'
import ChevronLeft from '~/components/icons/ChevronLeft'
import ChevronRight from '~/components/icons/ChevronRight'
import { ROUTES } from '~/constants/routes'
import { PARIS_TIME_ZONE } from '~/helpers/date'
import { useLocale } from '~/hooks/useLocale'
import { useNavigate } from '~/hooks/useNavigate'
import * as queries from '~/queries'
import { getSubscriptionLinkTo } from '~/utils/booking'
import { trackEvent } from '~/utils/tracking'
import { TZDate } from '@date-fns/tz'
import { useSuspenseQuery } from '@tanstack/react-query'
import type { SlotPickContext } from '../SlotPicker.types'
import { getCalendarOptions, getSlotsFromDay } from './SlotPickerNow.utils'
import SlotsHoursList from './SlotsHoursList'

export type SlotPickerNowProps = {
  context: SlotPickContext
  isInvoiceNeeded: boolean
  isLogged: boolean
  validateButtonLabel: string
  redirectTo?: string | null
}

type State = {
  selectedDay: Date
  selectedSlot: BookingSlot | null
}

const SlotPickerNow = ({
  context,
  isInvoiceNeeded,
  isLogged,
  validateButtonLabel,
  redirectTo = null
}: SlotPickerNowProps) => {
  const locale = useLocale()
  const navigate = useNavigate()

  const getOpenBookingSlotsQuery = useSuspenseQuery(
    queries.getOpenBookingSlotsOptions()
  )

  const calendarOptions = React.useMemo(() => {
    return getCalendarOptions(getOpenBookingSlotsQuery.data)
  }, [getOpenBookingSlotsQuery.data])

  const [state, setState] = React.useState<State>({
    selectedDay: new Date(calendarOptions.calendarDaysFromSlots[0]!),
    selectedSlot: null
  })
  const { selectedDay, selectedSlot } = state

  const [month, setMonth] = React.useState<Date>(calendarOptions.minDate)

  const handleNextMonthClick = () => {
    setMonth(
      add(month, {
        months: 1
      })
    )
  }

  const handlePreviousMonthClick = () => {
    setMonth(
      sub(month, {
        months: 1
      })
    )
  }

  const isPrevMonthDisabled =
    startOfMonth(new TZDate(month, PARIS_TIME_ZONE)) <=
    startOfMonth(new TZDate(calendarOptions.minDate, PARIS_TIME_ZONE))

  const isNextMonthDisabled =
    startOfMonth(new TZDate(month, PARIS_TIME_ZONE)) >=
    startOfMonth(new TZDate(calendarOptions.maxDate, PARIS_TIME_ZONE))

  const isDateUnavailable = (date: Date) => {
    if (calendarOptions && selectedDay) {
      return calendarOptions.matchIsDateUnavailable(date)
    }

    return true
  }

  const handleSelectDay = (date: Date | undefined) => {
    if (date) {
      trackEvent('SELECT_SLOT_DAY', { page: context })
      setState({
        selectedDay: date,
        selectedSlot: null
      })
    }
  }

  const handleSelectSlot = (slot: BookingSlot) => {
    trackEvent('SELECT_SLOT_HOUR', { page: context })
    setState((prevState) => {
      return {
        selectedDay: prevState.selectedDay,
        selectedSlot: slot
      }
    })
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!selectedSlot) {
      return
    }

    if (redirectTo) {
      navigate({
        pathname: redirectTo,
        search: `?slotId=${selectedSlot.id}`
      })

      return
    }

    const linkTo = getSubscriptionLinkTo({
      pathname: isLogged
        ? ROUTES.subscription_payment
        : ROUTES.subscription_myAccount,
      slotId: selectedSlot.id,
      isInvoiceNeeded
    })

    navigate(linkTo)
  }

  const availableSlots = selectedDay
    ? getSlotsFromDay(getOpenBookingSlotsQuery.data, selectedDay)
    : []

  const getCalendarLocale = () => {
    if (locale === 'en') {
      return enUS
    }

    return fr
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="grid h-full md:grid-cols-[60%,auto]">
        <div className="h-full w-full py-6 flex justify-center items-start">
          <DayPicker
            mode="single"
            animate
            locale={getCalendarLocale()}
            disabled={isDateUnavailable}
            startMonth={calendarOptions.minDate}
            endMonth={calendarOptions.maxDate}
            onSelect={handleSelectDay}
            month={month}
            selected={selectedDay}
            timeZone={PARIS_TIME_ZONE}
            weekStartsOn={1}
            components={{
              PreviousMonthButton: () => {
                return (
                  <IconButton
                    size="sm"
                    onClick={handlePreviousMonthClick}
                    disabled={isPrevMonthDisabled}
                    className="w-auto disabled:opacity-20"
                    color="secondary"
                    data-testid="previousMonth"
                  >
                    <ChevronLeft />
                  </IconButton>
                )
              },
              NextMonthButton: () => {
                return (
                  <IconButton
                    size="sm"
                    onClick={handleNextMonthClick}
                    disabled={isNextMonthDisabled}
                    className="w-auto disabled:opacity-20"
                    color="secondary"
                    data-testid="nextMonth"
                  >
                    <ChevronRight />
                  </IconButton>
                )
              }
            }}
            classNames={{
              day: 'w-9 h-9 m-0.5',
              day_button:
                'w-full h-full hover:bg-black/5 rounded-full border-1 border-black/20 disabled:border-0',
              selected: 'bg-primary text-white rounded-full w-10 h-10',
              today: 'text-primary',
              nav: 'w-full flex items-center justify-between absolute',
              month_caption: 'text-center p-2 text-sm font-medium',
              month: 'flex flex-col gap-y-2',
              weeks: 'flex flex-col gap-y-2',
              week: 'flex gap-x-2',
              weekdays: 'flex gap-x-2',
              weekday:
                'w-10 h-10 flex items-center justify-center font-normal text-primary',
              chevron: 'text-primary',
              disabled: 'text-black/20 pointer-events-none'
            }}
          />
        </div>
        <div className="relative h-full w-full border-black/10 md:border-l">
          <SlotsHoursList
            availableSlots={availableSlots}
            onSelectSlot={handleSelectSlot}
            selectedSlot={selectedSlot}
          />
        </div>
      </div>
      <div className="border-black/10 px-8 py-6 md:border-t">
        <Button
          rounded="full"
          type="submit"
          disabled={!(selectedDay && selectedSlot)}
          color="primary"
          fullWidth
          size="lg"
          testId="validate"
        >
          {validateButtonLabel}
        </Button>
      </div>
    </form>
  )
}

export default SlotPickerNow
